// src/components/Profile/ProfileBack.jsx
import React, { useState } from 'react';
import { TooltipContext } from './TooltipContext';
import { FileBadge, Database, Activity, Award, Settings } from 'lucide-react';
import PillList           from './PillList';
import InfoRow            from './InfoRow';
import CardEditRow        from './CardEditRow';
import { proficiencyChips } from './proficiencyChips';

// Areas of proficiency as canonical case types, with the ones a service
// names highlighted. The highlight is also spoken, never color alone.
const CaseTypeChips = ({ items, offered }) => (
  <ul className="crna-case-chips" aria-label="Areas of proficiency">
    {proficiencyChips(items).map((chip) => {
      const isOffered = offered.has(chip.key);
      return (
        <li
          key={chip.key}
          className={`crna-case-chip${isOffered ? ' crna-case-chip--offered' : ''}`}
        >
          <span>{chip.label}</span>
          {isOffered && <span className="visually-hidden">, named in a service</span>}
        </li>
      );
    })}
  </ul>
);

// The card back's rows, one descriptor each. Exported so the services owner
// preview names the rows employers do not see (an empty row is hidden from
// them) from the same definitions the card draws.
export function profileBackRows(p = {}) {
  const {
    emrExperience               = [],
    areasOfProficiency          = [],
    professionalCertifications  = [],
    activeCertifications        = [],
    stateMedicalLicenses        = [],
    stateAPRNLicenses           = [],
    stateLicenses               = [],
    facilityPreferences: {
      careTeamModel     = [],
      preferredSettings = []
    } = {},
  } = p;
  
  // Handle careTeamModel as both string (legacy) and array (new)
  const careModels = Array.isArray(careTeamModel) ? careTeamModel : (careTeamModel ? [careTeamModel] : []);

  // Combine medical + APRN + RN licenses, each state once (e.g., if someone
  // has both APRN-NV and RN-NV, only show NV once). A public card carries
  // medical licenses for an Anesthesiologist only (the sanitizer decides).
  const combinedLicenses = [
    ...new Set([...stateMedicalLicenses, ...stateAPRNLicenses, ...stateLicenses]),
  ];

  const licenseTooltip = it => {
    if (stateMedicalLicenses.includes(it)) {
      return `Medical License - ${it}`;
    }
    if (stateAPRNLicenses.includes(it)) {
      return `APRN License - ${it}`;
    }
    if (stateLicenses.includes(it)) {
      return `RN License - ${it}`;
    }
    return it;
  };

  const certifications = [...professionalCertifications, ...activeCertifications];
  const practicePrefs = [...careModels, ...preferredSettings].filter(Boolean);

  // One descriptor per row so the shared view can filter empties without a
  // trailing-divider mess. dashFallback preserves each row's historical empty
  // rendering (['—'] pill vs PillList's own empty span for preferences).
  return [
    { key: 'emr', label: 'EMR systems', Icon: Database, items: emrExperience, prompt: 'Add your EMR systems', getTooltip: (it) => `EMR: experience with ${it}`, dashFallback: true },
    { key: 'proficiency', label: 'areas of proficiency', Icon: Activity, items: areasOfProficiency, prompt: 'Add areas of proficiency', getTooltip: (it) => `Proficient in ${it} cases`, dashFallback: true },
    { key: 'certifications', label: 'certifications', Icon: Award, items: certifications, prompt: 'Add your certifications', getTooltip: (it) => `Certification: ${it}`, dashFallback: true },
    { key: 'licenses', label: 'state licenses', Icon: FileBadge, items: combinedLicenses, prompt: 'Add your state licenses', getTooltip: licenseTooltip, dashFallback: true },
    { key: 'preferences', label: 'practice preferences', Icon: Settings, items: practicePrefs, prompt: 'Add practice preferences', getTooltip: (it) => `Preference: ${it}`, dashFallback: false },
  ];
}

// onEditRow(key) is the anonymous card-builder seam: when present, every row
// here becomes tappable (keys: emr, proficiency, certifications, licenses,
// preferences) and empty rows read as "Add ..." prompts instead of a dash.
// hideEmptyRows is the shared-/s/-view seam: empty rows (and their dividers)
// are omitted entirely, so a sparse card reads as short rather than broken.
// Both absent -- every other viewer surface -- nothing changes.
// highlightCaseTypes (canonical slugs) is the services page seam: present, the
// proficiency row renders canonical case-type chips and marks those slugs as
// named in a service. Absent, the row keeps its pills exactly as before. highlightCaption
// is printed under those chips when both are given.
// verifiedMarks ({ rowKey: markText }) marks the rows RxRooster verified with
// what it verified, in the details presentation only (the services page
// clinical section).
const ProfileBack = ({ p = {}, onEditRow = null, hideEmptyRows = false, presentation = 'card', highlightCaseTypes = null, highlightCaption = null, verifiedMarks = null }) => {
  const [openId, setOpenId] = useState(null);
  const offered = Array.isArray(highlightCaseTypes) ? new Set(highlightCaseTypes) : null;
  const markFor = (key) =>
    verifiedMarks && typeof verifiedMarks[key] === 'string' && verifiedMarks[key]
      ? verifiedMarks[key]
      : null;
  const RowWrapper = presentation === 'details' ? 'div' : React.Fragment;
  const edit = (key) => (onEditRow ? () => onEditRow(key) : null);
  const rowDefs = profileBackRows(p);
  const rows = hideEmptyRows ? rowDefs.filter((r) => r.items.length > 0) : rowDefs;

  const content = (
    <div className={presentation === 'details' ? 'crna-service-clinical' : 'card-face-content'} role="region" aria-label="Profile Back">
      {rows.length === 0 && (
        <p className="crna-back-empty">This provider hasn&apos;t added extra details yet.</p>
      )}
      {rows.map((r, i) => (
        <RowWrapper key={r.key} {...(presentation === 'details' ? { className: 'crna-service-clinical-item' } : {})}>
          {i > 0 && presentation !== 'details' && <hr className="hand-drawn-divider" />}
          {presentation === 'details' && (
            <p className="crna-service-clinical-label">
              <span>{r.key === 'licenses' ? 'State licenses listed' : r.key === 'certifications' ? 'Certifications listed' : r.label}</span>
              {markFor(r.key) && <span className="crna-verified-mark">{markFor(r.key)}</span>}
            </p>
          )}
          <CardEditRow onEdit={edit(r.key)} label={r.label} Icon={r.Icon} empty={!r.items.length} prompt={r.prompt}>
            <InfoRow Icon={r.Icon}>
              {offered && r.key === 'proficiency' && r.items.length ? (
                <CaseTypeChips items={r.items} offered={offered} />
              ) : (
              <PillList
                items={r.items.length || !r.dashFallback ? r.items : ['—']}
                getTooltip={r.getTooltip}
                idPrefix={r.key}
              />
              )}
            </InfoRow>
          </CardEditRow>
          {offered && highlightCaption && r.key === 'proficiency' && r.items.length > 0 && (
            <p className="crna-case-chips-caption">{highlightCaption}</p>
          )}
        </RowWrapper>
      ))}
    </div>
  );
  return presentation === 'details' ? <TooltipContext.Provider value={{ openId, setOpenId }}>{content}</TooltipContext.Provider> : content;
};

export default ProfileBack;
